/**
 * Pagination - Pagination controls component.
 * Phase 19: Enhanced Frontend UI.
 */

import { useLocale } from '../contexts/LocaleContext'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  className?: string
}

export function Pagination({ currentPage, totalPages, onPageChange, className = '' }: PaginationProps) {
  const { t } = useLocale()
  if (totalPages <= 1) return null
  
  const pages: (number | string)[] = []
  
  // Always show first page
  pages.push(1)
  
  // Show pages around current page
  if (currentPage > 3) {
    pages.push('...')
  }
  
  for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
    pages.push(i)
  }
  
  // Show ellipsis and last page if needed
  if (currentPage < totalPages - 2) {
    pages.push('...')
  }
  
  if (totalPages > 1) {
    pages.push(totalPages)
  }
  
  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="btn btn-sm btn-secondary"
        aria-label={t('audits.prevAria')}
      >
        ←
      </button>
      
      {pages.map((page, index) => {
        if (page === '...') {
          return (
            <span key={`ellipsis-${index}`} className="px-2 text-[var(--text-tertiary)]">
              ...
            </span>
          )
        }
        
        const pageNum = page as number
        const isActive = pageNum === currentPage
        
        return (
          <button
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-secondary'}`}
            aria-label={t('common.pageAria', { page: pageNum })}
            aria-current={isActive ? 'page' : undefined}
          >
            {pageNum}
          </button>
        )
      })}
      
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="btn btn-sm btn-secondary"
        aria-label={t('audits.nextAria')}
      >
        →
      </button>
    </div>
  )
}
